import { getJobPositionStatusColor, getJobPositionStatusLabel, Typography } from "@cpj-ai/panel-npm-package";
import { Card, CardBody, Chip } from "@heroui/react";
import dayjs from "dayjs";
import { TJobPosition, TJobPositionAssignmentItem } from ".";

interface IJobPositionInfoCardProps {
  jobPosition?: TJobPosition | null;
  assignment?: TJobPositionAssignmentItem | null;
}
const JobPositionInfoCard = ({ jobPosition, assignment }: IJobPositionInfoCardProps) => {
  const formatEnum = (enumString: string | null | undefined) => {
    if (!enumString) return "";
    return enumString.replace(/_/g, " ").toLowerCase();
  };

  return (
    <Card className="mb-6 border-l-4 border-l-primary shadow-sm" isPressable>
      <CardBody>
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
          <div>
            <div className="flex items-center justify-between md:justify-start gap-2 mb-2">
              <Typography className="font-semibold text-lg">{jobPosition?.title}</Typography>
              <Chip size="sm" className={getJobPositionStatusColor(jobPosition?.status)} variant="flat">
                {getJobPositionStatusLabel(jobPosition?.status) ?? "Not set"}
              </Chip>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Payment Term:</span>
                <span className="font-medium capitalize">{formatEnum(jobPosition?.paymentTerm)}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Applied:</span>
                <span className="font-medium">{dayjs(assignment?.createdAt).format("MMM D, YYYY")}</span>
              </div>
              {jobPosition?.contractType && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Contract:</span>
                  <span className="font-medium capitalize">{formatEnum(jobPosition?.contractType)}</span>
                </div>
              )}
              {jobPosition?.locationType && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Location:</span>
                  <span className="font-medium capitalize">{formatEnum(jobPosition?.locationType)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {jobPosition?.skills?.length
              ? jobPosition?.skills?.slice(0, 3).map((skill) => (
                  <Chip key={skill?.title} size="sm" variant="flat" color="default">
                    {skill?.title}
                  </Chip>
                ))
              : ""}
            {jobPosition?.skills?.length && jobPosition?.skills.length > 3 ? (
              <Chip size="sm" variant="flat" color="default">
                +{jobPosition?.skills.length - 3} more
              </Chip>
            ) : (
              ""
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
          <StatusCounter title="Validity Days" value={jobPosition?.validityDays ?? 0} isText />
          <StatusCounter title="Vaccancy" value={jobPosition?.vaccancy ?? 0} isText />
          {jobPosition?.startAt && (
            <StatusCounter
              title="Starts in"
              isText={true}
              value={(() => {
                const daysDiff = dayjs(jobPosition?.startAt).diff(dayjs(), "days");
                if (daysDiff < 0) {
                  return `${Math.abs(daysDiff)} days ago`;
                }
                return daysDiff;
              })()}
            />
          )}
          <StatusCounter
            title="Salary Range"
            value={`$${jobPosition?.salaryRange?.[0]} - $${jobPosition?.salaryRange?.[1]}`}
            isText
          />
        </div>
      </CardBody>
    </Card>
  );
};

interface StatusCounterProps {
  title: string;
  value: number | string;
  isText?: boolean;
}

const StatusCounter: React.FC<StatusCounterProps> = ({ title, value, isText = false }) => {
  return (
    <div className="flex flex-col">
      <Typography variant="muted" className="text-xs">
        {title}
      </Typography>
      <Typography className={`font-semibold ${isText ? "text-sm" : "text-lg"} text-zinc-700`}>{value}</Typography>
    </div>
  );
};
export default JobPositionInfoCard;
